import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Tabs,
  Tab,
  LinearProgress,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Compare as CompareIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  AttachMoney as AttachMoneyIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Download as DownloadIcon,
  Settings as SettingsIcon,
  Insights as InsightsIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  Speed as SpeedIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Analytics as AnalyticsIcon,
  ShowChart as ShowChartIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';

interface Scenario {
  _id: string;
  name: string;
  description: string;
  project: string;
  type: 'baseline' | 'optimistic' | 'pessimistic' | 'custom';
  status: 'draft' | 'active' | 'completed' | 'archived';
  parameters: {
    duration: number;
    budget: number;
    resources: number;
    riskFactor: number;
    qualityLevel: number;
  };
  results: {
    projectedCost: number;
    projectedDuration: number;
    riskScore: number;
    successProbability: number;
    criticalPath: string[];
    resourceUtilization: number;
    qualityMetrics: {
      defectRate: number;
      reworkPercentage: number;
      customerSatisfaction: number;
    };
  };
  createdAt: string;
  updatedAt: string;
}

interface ComparisonMetric {
  key: string;
  label: string;
  format: 'currency' | 'percentage' | 'number' | 'days';
  getValue: (scenario: Scenario) => number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scenario-tabpanel-${index}`}
      aria-labelledby={`scenario-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ScenarioComparison: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  
  // Get planId from query parameters using useMemo to prevent infinite re-renders
  const planId = React.useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('planId');
  }, []);
  
  // Debug logging for URL parameters
  console.log('🔍 [Scenario Comparison] Component mounted/updated');
  console.log('🔍 [Scenario Comparison] Current URL:', window.location.href);
  console.log('🔍 [Scenario Comparison] URL pathname:', window.location.pathname);
  console.log('🔍 [Scenario Comparison] URL search params:', window.location.search);
  console.log('🔍 [Scenario Comparison] projectId from useParams:', projectId);
  console.log('🔍 [Scenario Comparison] planId from query params:', planId);
  console.log('🔍 [Scenario Comparison] Both projectId and planId available:', !!(projectId || planId));
  
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [selectedScenarios, setSelectedScenarios] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [runningScenario, setRunningScenario] = useState<string | null>(null);
  const [comparisonView, setComparisonView] = useState<'table' | 'chart'>('table');
  const [newScenario, setNewScenario] = useState({
    name: '',
    description: '',
    type: 'custom' as const,
    parameters: {
      duration: 0,
      budget: 0,
      resources: 0,
      riskFactor: 0.5,
      qualityLevel: 0.8
    }
  });

  const comparisonMetrics: ComparisonMetric[] = [
    {
      key: 'projectedCost',
      label: 'Projected Cost',
      format: 'currency',
      getValue: (scenario) => scenario.results?.projectedCost || 0
    },
    {
      key: 'projectedDuration',
      label: 'Projected Duration',
      format: 'days',
      getValue: (scenario) => scenario.results?.projectedDuration || 0
    },
    {
      key: 'riskScore',
      label: 'Risk Score',
      format: 'number',
      getValue: (scenario) => scenario.results?.riskScore || 0
    },
    {
      key: 'successProbability',
      label: 'Success Probability',
      format: 'percentage',
      getValue: (scenario) => scenario.results?.successProbability || 0
    },
    {
      key: 'resourceUtilization',
      label: 'Resource Utilization',
      format: 'percentage',
      getValue: (scenario) => scenario.results?.resourceUtilization || 0
    },
    {
      key: 'defectRate',
      label: 'Defect Rate',
      format: 'percentage',
      getValue: (scenario) => scenario.results?.qualityMetrics?.defectRate || 0
    }
  ];

  const fetchScenarios = useCallback(async () => {
    console.log('🔄 [Scenario Comparison] Starting fetchScenarios...');
    console.log('🔄 [Scenario Comparison] Current loading state:', loading);
    
    try {
      console.log('🔄 [Scenario Comparison] Setting loading to true');
      setLoading(true);
      setError(null);
      
      // Fetch all scenarios without filtering by project/plan
      const url = '/scenarios';
      
      console.log('🔄 [Scenario Comparison] Final API URL:', url);
      console.log('🔄 [Scenario Comparison] Making API call to fetch all scenarios...');
      
      const response = await api.get(url);
      console.log('✅ [Scenario Comparison] API call successful!');
      console.log('✅ [Scenario Comparison] Response status:', response.status);
      console.log('✅ [Scenario Comparison] Response data:', response.data);
      
      const scenariosData = response.data.data || [];
      console.log('✅ [Scenario Comparison] Extracted scenarios data:', scenariosData);
      console.log('✅ [Scenario Comparison] Number of scenarios found:', scenariosData.length);
      
      setScenarios(scenariosData);
      console.log('✅ [Scenario Comparison] Scenarios state updated');
      
      // Auto-select first two scenarios for comparison
      if (scenariosData.length >= 2) {
        const selectedIds = [scenariosData[0]._id, scenariosData[1]._id];
        setSelectedScenarios(selectedIds);
        console.log('✅ [Scenario Comparison] Auto-selected scenarios:', selectedIds);
      } else if (scenariosData.length === 1) {
        setSelectedScenarios([scenariosData[0]._id]);
        console.log('✅ [Scenario Comparison] Selected single scenario:', scenariosData[0]._id);
      } else {
        console.log('⚠️ [Scenario Comparison] No scenarios found to select');
      }
      
      console.log('✅ [Scenario Comparison] Setting loading to false');
      setLoading(false);
      console.log('✅ [Scenario Comparison] fetchScenarios completed successfully');
    } catch (err: any) {
      console.error('❌ [Scenario Comparison] Error in fetchScenarios:', err);
      console.error('❌ [Scenario Comparison] Error response:', err.response);
      console.error('❌ [Scenario Comparison] Error message:', err.message);
      console.error('❌ [Scenario Comparison] Error status:', err.response?.status);
      console.error('❌ [Scenario Comparison] Error data:', err.response?.data);
      
      const errorMessage = err.response?.data?.message || 'Failed to fetch scenarios';
      console.error('❌ [Scenario Comparison] Setting error message:', errorMessage);
      setError(errorMessage);
      console.error('❌ [Scenario Comparison] Error state updated');
    } finally {
      console.log('🔄 [Scenario Comparison] Finally block - setting loading to false');
      setLoading(false);
      console.log('🔄 [Scenario Comparison] Loading state updated in finally block');
    }
  }, [projectId, planId]);

  useEffect(() => {
    console.log('🔄 [Scenario Comparison] useEffect triggered');
    console.log('🔄 [Scenario Comparison] useEffect - projectId:', projectId);
    console.log('🔄 [Scenario Comparison] useEffect - planId:', planId);
    
    // Always fetch all scenarios on component mount
    console.log('🔄 [Scenario Comparison] useEffect - calling fetchScenarios');
    fetchScenarios();
  }, [fetchScenarios]);

  const handleCreateScenario = async () => {
    try {
      const scenarioData = {
        ...newScenario,
        ...(planId ? { plan: planId } : { project: projectId })
      };
      
      console.log('Creating scenario with data:', scenarioData);
      const response = await api.post('/scenarios', scenarioData);
      
      setCreateDialogOpen(false);
      setNewScenario({
        name: '',
        description: '',
        type: 'custom',
        parameters: {
          duration: 0,
          budget: 0,
          resources: 0,
          riskFactor: 0.5,
          qualityLevel: 0.8
        }
      });
      
      fetchScenarios();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create scenario');
    }
  };

  const handleRunScenario = async (scenarioId: string) => {
    try {
      setRunningScenario(scenarioId);
      await api.post(`/scenarios/${scenarioId}/run`);
      
      // Poll for updates
      const pollInterval = setInterval(async () => {
        try {
          const response = await api.get(`/scenarios/${scenarioId}`);
          const scenario = response.data.data;
          
          setScenarios(prev => prev.map(s => s._id === scenarioId ? scenario : s));
          
          if (scenario.status === 'completed') {
            clearInterval(pollInterval);
            setRunningScenario(null);
          }
        } catch (err) {
          clearInterval(pollInterval);
          setRunningScenario(null);
        }
      }, 2000);
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to run scenario');
      setRunningScenario(null);
    }
  };

  const handleScenarioSelection = (scenarioId: string) => {
    setSelectedScenarios(prev => {
      if (prev.includes(scenarioId)) {
        return prev.filter(id => id !== scenarioId);
      } else if (prev.length < 4) { // Limit to 4 scenarios for comparison
        return [...prev, scenarioId];
      }
      return prev;
    });
  };

  const formatValue = (value: number, format: string) => {
    switch (format) {
      case 'currency':
        return `$${value.toLocaleString()}`;
      case 'percentage':
        return `${(value * 100).toFixed(1)}%`;
      case 'days':
        return `${value}d`;
      case 'number':
        return value.toFixed(2);
      default:
        return value.toString();
    }
  };

  const getComparisonIcon = (current: number, baseline: number, format: string) => {
    const isHigherBetter = format === 'percentage';
    const difference = current - baseline;
    
    if (Math.abs(difference) < 0.01) {
      return null;
    }
    
    const isPositive = isHigherBetter ? difference > 0 : difference < 0;
    return isPositive ? 
      <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} /> : 
      <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />;
  };

  const renderScenarioList = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Available Scenarios ({scenarios.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          New Scenario
        </Button>
      </Box>
      
      <List>
        {scenarios.map((scenario) => (
          <ListItem
            key={scenario._id}
            divider
            sx={{
              cursor: 'pointer',
              backgroundColor: selectedScenarios.includes(scenario._id) ? 'action.selected' : 'inherit',
              '&:hover': { backgroundColor: 'action.hover' }
            }}
          >
            <ListItemIcon>
              <Checkbox
                checked={selectedScenarios.includes(scenario._id)}
                onChange={() => handleScenarioSelection(scenario._id)}
                disabled={!selectedScenarios.includes(scenario._id) && selectedScenarios.length >= 4}
              />
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1">
                    {scenario.name}
                  </Typography>
                  <Chip
                    label={scenario.type}
                    size="small"
                    variant="outlined"
                    color={scenario.type === 'baseline' ? 'primary' : 'default'}
                  />
                  <Chip
                    label={scenario.status}
                    size="small"
                    color={
                      scenario.status === 'completed' ? 'success' :
                      scenario.status === 'active' ? 'primary' :
                      scenario.status === 'draft' ? 'warning' : 'default'
                    }
                  />
                  {runningScenario === scenario._id && (
                    <CircularProgress size={16} />
                  )}
                </Box>
              }
              secondary={
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {scenario.description}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Created: {new Date(scenario.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
              }
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              {scenario.status === 'draft' && (
                <Tooltip title="Run Scenario">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRunScenario(scenario._id);
                    }}
                    disabled={runningScenario === scenario._id}
                  >
                    <PlayArrowIcon />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                }}
              >
                <MoreVertIcon />
              </IconButton>
            </Box>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const renderComparisonTable = () => {
    const selectedScenarioData = scenarios.filter(s => selectedScenarios.includes(s._id));
    
    if (selectedScenarioData.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">
            Select scenarios to compare
          </Typography>
        </Box>
      );
    }

    const baselineScenario = selectedScenarioData.find(s => s.type === 'baseline') || selectedScenarioData[0];

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Metric</TableCell>
              {selectedScenarioData.map((scenario) => (
                <TableCell key={scenario._id} align="center">
                  <Box>
                    <Typography variant="subtitle2">
                      {scenario.name}
                    </Typography>
                    <Chip
                      label={scenario.type}
                      size="small"
                      variant="outlined"
                      color={scenario.type === 'baseline' ? 'primary' : 'default'}
                    />
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {comparisonMetrics.map((metric) => (
              <TableRow key={metric.key}>
                <TableCell component="th" scope="row">
                  <Typography variant="subtitle2">
                    {metric.label}
                  </Typography>
                </TableCell>
                {selectedScenarioData.map((scenario) => {
                  const value = metric.getValue(scenario);
                  const baselineValue = metric.getValue(baselineScenario);
                  const icon = scenario._id !== baselineScenario._id ? 
                    getComparisonIcon(value, baselineValue, metric.format) : null;
                  
                  return (
                    <TableCell key={scenario._id} align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <Typography variant="body2">
                          {formatValue(value, metric.format)}
                        </Typography>
                        {icon}
                      </Box>
                      {scenario._id !== baselineScenario._id && (
                        <Typography variant="caption" color="textSecondary">
                          {value > baselineValue ? '+' : ''}
                          {formatValue(value - baselineValue, metric.format)}
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderSummaryCards = () => {
    const selectedScenarioData = scenarios.filter(s => selectedScenarios.includes(s._id));
    
    if (selectedScenarioData.length === 0) {
      return null;
    }

    const bestCost = Math.min(...selectedScenarioData.map(s => s.results?.projectedCost || Infinity));
    const bestDuration = Math.min(...selectedScenarioData.map(s => s.results?.projectedDuration || Infinity));
    const bestRisk = Math.min(...selectedScenarioData.map(s => s.results?.riskScore || Infinity));
    const bestSuccess = Math.max(...selectedScenarioData.map(s => s.results?.successProbability || 0));

    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Best Cost
                </Typography>
                <Typography variant="h6">
                  {formatValue(bestCost, 'currency')}
                </Typography>
              </Box>
              <AttachMoneyIcon color="success" sx={{ fontSize: 32 }} />
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Best Duration
                </Typography>
                <Typography variant="h6">
                  {formatValue(bestDuration, 'days')}
                </Typography>
              </Box>
              <ScheduleIcon color="primary" sx={{ fontSize: 32 }} />
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Lowest Risk
                </Typography>
                <Typography variant="h6">
                  {formatValue(bestRisk, 'number')}
                </Typography>
              </Box>
              <WarningIcon color="warning" sx={{ fontSize: 32 }} />
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Best Success Rate
                </Typography>
                <Typography variant="h6">
                  {formatValue(bestSuccess, 'percentage')}
                </Typography>
              </Box>
              <CheckCircleIcon color="success" sx={{ fontSize: 32 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading scenarios...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            Scenario Comparison
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Compare different project scenarios and their outcomes
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={comparisonView === 'chart'}
                onChange={(e) => setComparisonView(e.target.checked ? 'chart' : 'table')}
              />
            }
            label="Chart View"
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchScenarios}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            New Scenario
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 2fr' }, gap: 3 }}>
        {/* Scenario List */}
        <Paper sx={{ p: 2, height: 'fit-content' }}>
          {renderScenarioList()}
        </Paper>

        {/* Comparison Results */}
        <Paper>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab label="Comparison" />
              <Tab label="Analysis" />
              <Tab label="Recommendations" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            {renderSummaryCards()}
            {comparisonView === 'table' ? renderComparisonTable() : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <ShowChartIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                <Typography variant="h6" color="textSecondary">
                  Chart visualization would be implemented here
                </Typography>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Scenario Analysis
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Detailed analysis and insights for selected scenarios would be displayed here.
              </Typography>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Recommendations
              </Typography>
              <Typography variant="body2" color="textSecondary">
                AI-powered recommendations based on scenario comparison would be shown here.
              </Typography>
            </Box>
          </TabPanel>
        </Paper>
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => {
          setAnchorEl(null);
        }}>
          <VisibilityIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => {
          setAnchorEl(null);
        }}>
          <EditIcon sx={{ mr: 1 }} />
          Edit Scenario
        </MenuItem>
        <MenuItem onClick={() => {
          setAnchorEl(null);
        }}>
          <DownloadIcon sx={{ mr: 1 }} />
          Export Report
        </MenuItem>
        <MenuItem onClick={() => {
          setAnchorEl(null);
        }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete Scenario
        </MenuItem>
      </Menu>

      {/* Create Scenario Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Scenario</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Scenario Name"
              value={newScenario.name}
              onChange={(e) => setNewScenario(prev => ({ ...prev, name: e.target.value }))}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={newScenario.description}
              onChange={(e) => setNewScenario(prev => ({ ...prev, description: e.target.value }))}
              sx={{ mb: 2 }}
            />
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Scenario Type</InputLabel>
              <Select
                value={newScenario.type}
                onChange={(e) => setNewScenario(prev => ({ ...prev, type: e.target.value as any }))}
              >
                <MenuItem value="baseline">Baseline</MenuItem>
                <MenuItem value="optimistic">Optimistic</MenuItem>
                <MenuItem value="pessimistic">Pessimistic</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <Box sx={{ width: { xs: '100%', md: '50%' } }}>
                <TextField
                  fullWidth
                  label="Duration (days)"
                  type="number"
                  value={newScenario.parameters.duration}
                  onChange={(e) => setNewScenario(prev => ({
                    ...prev,
                    parameters: { ...prev.parameters, duration: Number(e.target.value) }
                  }))}
                />
              </Box>
              <Box sx={{ width: { xs: '100%', md: '50%' } }}>
                <TextField
                  fullWidth
                  label="Planned Budget"
                  type="number"
                  value={newScenario.parameters.budget}
                  onChange={(e) => setNewScenario(prev => ({
                    ...prev,
                    parameters: { ...prev.parameters, budget: Number(e.target.value) }
                  }))}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateScenario}>
            Create Scenario
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScenarioComparison;